import api from './client'
import type {
  AccountingFilters,
  AccountingSummary,
  Expense,
  ExpenseSettlement,
  ExpenseType,
  User,
} from '../types'

export type ExpenseTypePayload = {
  name: string
  description?: string
  isActive?: boolean
}

export type CreateExpensePayload = {
  expenseTypeId: string
  amount: number | string
  notes?: string
  noReceipt: boolean
  receipt?: File | null
}

export type CreateSettlementPayload = {
  userId: string
  amount: number | string
  expenseIds?: string[]
  notes?: string
  settledAt?: string
  periodStart?: string
  periodEnd?: string
}

export type SettlementCandidatesResponse = {
  user: User
  availableCredit: number
  outstandingTotal: number
  recommendedPaymentAmount: number
  expenses: Expense[]
}

function buildFilterParams(filters?: AccountingFilters) {
  if (!filters) return undefined
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')
  )
}

function toExpenseFormData(data: CreateExpensePayload): FormData {
  const formData = new FormData()
  formData.set('expenseTypeId', data.expenseTypeId)
  formData.set('amount', String(data.amount))
  formData.set('noReceipt', String(data.noReceipt))
  if (data.notes?.trim()) formData.set('notes', data.notes.trim())
  if (data.receipt) formData.set('receipt', data.receipt)
  return formData
}

export const accountingApi = {
  listExpenseTypes: () =>
    api.get<{ data: ExpenseType[] }>('/accounting/expense-types'),

  createExpenseType: (payload: ExpenseTypePayload) =>
    api.post<{ data: ExpenseType }>('/accounting/expense-types', payload),

  updateExpenseType: (id: string, payload: ExpenseTypePayload) =>
    api.put<{ data: ExpenseType }>(`/accounting/expense-types/${id}`, payload),

  deleteExpenseType: (id: string) =>
    api.delete<{ data: { message: string } }>(`/accounting/expense-types/${id}`),

  listExpenses: (filters?: AccountingFilters) =>
    api.get<{ data: Expense[] }>('/accounting/expenses', { params: buildFilterParams(filters) }),

  createExpense: (payload: CreateExpensePayload) =>
    api.post<{ data: Expense }>('/accounting/expenses', toExpenseFormData(payload)),

  deleteExpense: (id: string) =>
    api.delete<{ data: { message: string } }>(`/accounting/expenses/${id}`),

  listSettlements: (filters?: AccountingFilters) =>
    api.get<{ data: ExpenseSettlement[] }>('/accounting/settlements', { params: buildFilterParams(filters) }),

  getSettlementCandidates: (userId: string) =>
    api.get<{ data: SettlementCandidatesResponse }>('/accounting/settlement-candidates', { params: { userId } }),

  createSettlement: (payload: CreateSettlementPayload) =>
    api.post<{ data: ExpenseSettlement }>('/accounting/settlements', payload),

  getSummary: (filters?: AccountingFilters) =>
    api.get<{ data: AccountingSummary }>('/accounting/summary', { params: buildFilterParams(filters) }),
}
